import { legacyAPIRequest } from '../api/legacyAPIRequest';
import { restApi } from '../api/restApiSlice';

import CachedPage, { AdaptiveType } from './CacheManagerTab/CachedPage';
import { CachedPagesQueryParameters } from './CacheManagerTab/QueryParameters';
import URLParameter from './URLParametersTab/URLParameter';
import { ClearCacheStatus } from '../newClearCache/NewClearCacheModal';

export type CachedPagesPagination = {
  page: number;
  pageSize: number;
};

type CachedPageQuery = CachedPagesQueryParameters & CachedPagesPagination;

type AddURLsBody = {
  url: string;
  adaptiveType: AdaptiveType;
}[];

type AddURLsResponse = {
  data: {
    result: {
      item: {
        url: string;
        adaptiveType: AdaptiveType;
      };

      error?: {
        message: string;
        urlAlreadyExists: boolean;
      };
    }[];
  };
};

export const enum CachedCsvExportStatus {
  QUEUED = 'queued',
  NOT_QUEUED = 'not queued',
  ALREADY_EXISTS = 'already exists',
  LIMITED = 'limited',
}

type ExportCsvResponse = {
  status: CachedCsvExportStatus;
};

const CacheManagerApi = restApi.injectEndpoints({
  endpoints: (builder) => ({
    // Cache Manager TAB

    getCachedPagesList: builder.query<CachedPage[], CachedPageQuery>({
      queryFn: async (params) => {
        try {
          const parsedParams = Object.entries(params).reduce((acc, [key, value]) => {
            if (Array.isArray(value)) {
              acc[key] = value.join(',');
            } else {
              acc[key] = value;
            }
            return acc;
          }, {} as Record<string, string | number>);

          const searchParams = new URLSearchParams({
            ...parsedParams,
            page: params.page.toString(),
            pageSize: params.pageSize.toString(),
          });
          const rawResult = await legacyAPIRequest({ url: `cached-pages?${searchParams}`, method: 'GET' });
          if (!rawResult.ok) throw new Error('Failed to fetch cached pages');
          const result = await rawResult.json();
          return { data: result };
        } catch (err) {
          const error = err as Error;
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      providesTags: ['CachedPages'],
    }),

    addURLs: builder.mutation<AddURLsResponse, AddURLsBody>({
      query: (urls) => {
        return { url: '/v3/urls', method: 'POST', body: { urls } };
      },
      invalidatesTags: ['CachedPages', 'URL'],
    }),

    updateCacheFreshness: builder.mutation<true, { value: number }>({
      queryFn: async (body) => {
        try {
          const result = await legacyAPIRequest({ url: `set-cache-freshness`, method: 'POST', body });
          if (result.ok) return { data: true };
          throw new Error('Failed to update cache freshness');
        } catch (err) {
          const error = err as Error;
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
    }),

    exportURLsAsCSV: builder.mutation<ExportCsvResponse, CachedPagesQueryParameters>({
      query: (params) => ({
        url: '/v3/cached-pages-csv-export',
        method: 'POST',
        params,
      }),
    }),

    exportURLsAsSitemap: builder.mutation<string, void>({
      queryFn: async () => {
        try {
          const rawResult = await legacyAPIRequest({ url: 'v2/generate-sitemap', method: 'GET' });
          if (!rawResult.ok) throw new Error('Failed to generate sitemap');

          const result = await rawResult.text();

          return { data: result };
        } catch (err) {
          const error = err as Error;
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
    }),

    recacheURLs: builder.mutation<true, { ids: string[] }>({
      queryFn: async (body) => {
        try {
          const result = await legacyAPIRequest({ url: 'v2/cache-page', method: 'POST', body });
          if (result.ok) return { data: true };
          throw new Error('Failed to recache URLs');
        } catch (err) {
          const error = err as Error;
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      invalidatesTags: ['CachedPages'],
    }),

    deleteURLs: builder.mutation<true, { ids: string[] }>({
      queryFn: async (body) => {
        try {
          const result = await legacyAPIRequest({ url: 'remove-cached-urls', method: 'DELETE', body });
          if (result.ok) return { data: true };
          throw new Error('Failed to delete URLs');
        } catch (err) {
          const error = err as Error;
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      invalidatesTags: ['CachedPages'],
    }),

    seoAudit: builder.mutation({
      query: (url: string) => ({ url: '/v3/seo-audit', method: 'POST', body: { url } }),
      invalidatesTags: ['CachedPages', 'URL'],
    }),

    clearCache: builder.mutation<ClearCacheStatus, void>({
      async queryFn() {
        try {
          const result = await legacyAPIRequest({ url: 'v2/cache-pages', method: 'DELETE' });
          if (!result.ok) {
            throw new Error('Failed to clear cache');
          }
          return { data: { status: 'clear_pages_queued' } };
        } catch (err) {
          const error = err as Error;
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      invalidatesTags: ['CachedPages', 'URL'],
    }),

    // URL Parameters TAB

    getURLParameters: builder.query<URLParameter[], void>({
      queryFn: async () => {
        try {
          const result = await legacyAPIRequest({ url: 'url-parameters', method: 'GET' });
          const data = await result.json();
          if (result.ok) return { data };
          throw new Error('Failed to fetch URL parameters');
        } catch (err) {
          const error = err as Error;
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      providesTags: ['URLParameters'],
    }),

    createURLParameter: builder.mutation<true, Pick<URLParameter, 'value' | 'ignore'>>({
      queryFn: async (body) => {
        try {
          const result = await legacyAPIRequest({ url: 'url-parameters', method: 'PUT', body });
          if (result.ok) return { data: true };
          throw new Error('Failed to create URL parameter');
        } catch (err) {
          const error = err as Error;
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      invalidatesTags: ['URLParameters'],
    }),

    updateURLParameter: builder.mutation<true, Pick<URLParameter, 'id' | 'value' | 'ignore'>[]>({
      queryFn: async (urlParams) => {
        try {
          const result = await legacyAPIRequest({ url: 'v2/url-parameters', method: 'PUT', body: { urlParams } });
          if (result.ok) return { data: true };
          throw new Error('Failed to update URL parameter');
        } catch (err) {
          const error = err as Error;
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      invalidatesTags: ['URLParameters'],
    }),

    deleteURLParameter: builder.mutation<true, URLParameter['id'][]>({
      queryFn: async (ids) => {
        try {
          const result = await legacyAPIRequest({ url: 'v2/url-parameters', method: 'DELETE', body: { ids } });
          if (result.ok) return { data: true };
          throw new Error('Failed to delete URL parameter');
        } catch (err) {
          const error = err as Error;
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      invalidatesTags: ['URLParameters'],
    }),
  }),
});

export const {
  // Cache Manager TAB
  useGetCachedPagesListQuery,
  useAddURLsMutation,
  useUpdateCacheFreshnessMutation,
  useExportURLsAsCSVMutation,
  useExportURLsAsSitemapMutation,
  useRecacheURLsMutation,
  useDeleteURLsMutation,
  useClearCacheMutation,
  // URL Parameters TAB
  useGetURLParametersQuery,
  useCreateURLParameterMutation,
  useUpdateURLParameterMutation,
  useDeleteURLParameterMutation,
  useSeoAuditMutation,
} = CacheManagerApi;
